import { NgxsModule } from "@ngxs/store";
import { CommonModule } from "@angular/common";
import { BrowserModule } from "@angular/platform-browser";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { NgxsStoragePluginModule, StorageOption } from "@ngxs/storage-plugin";
import { APP_INITIALIZER, NgModule, Optional, SkipSelf } from "@angular/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ROOMS_LIB_CONFIG, RoomsLibModule, deserializeMoment } from "@3volutionsag/ngx-rooms-lib";
import { MbscNotificationsModule } from "@mobiscroll/angular";
import { User } from "oidc-client-ts";

import { environment } from "src/environments/environment";

import { BookingState } from "./booking/state/booking.state";
import { ConfigurationState } from "./configuration/state/configuration.state";
import { AuthenticationState } from "./authentication/state/authentication.state";
import { DynamicAssetState } from "./dynamic-assets/dynamic-asset.state";
import { ResourceState } from "./resource/state/resource.state";
import { ConfigurationService } from "./configuration/configuration.service";
import { AuthInterceptor } from "./authentication/auth.interceptor";

import type { RoomsLibConfig } from "@3volutionsag/ngx-rooms-lib";

export function initializeApp(configService: ConfigurationService) {
	return async () => {
		const config = await configService.loadConfig();
		configService.config = config;
	};
}

@NgModule({
	declarations: [],
	imports: [
		CommonModule,
		BrowserModule,
		MbscNotificationsModule,
		BrowserAnimationsModule,
		NgxsModule.forRoot(
			[ConfigurationState, AuthenticationState, BookingState, ResourceState, DynamicAssetState],
			{
				developmentMode: !environment.production,
			},
		),
		NgxsStoragePluginModule.forRoot({
			keys: "*",
			beforeSerialize: (state) => {
				// Exclude calendar and home from persistence
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { calendar, home, ...stateToPersist } = state;

				const serviceUser = stateToPersist?.authentication?.serviceUser as User | null;
				if (serviceUser !== null) {
					const { access_token, refresh_token, token_type, expires_at, id_token } = serviceUser;

					return {
						...stateToPersist,
						authentication: {
							serviceUser: {
								access_token,
								refresh_token,
								token_type,
								expires_at,
								id_token,
							},
						},
					};
				}
				return stateToPersist;
			},
			storage: StorageOption.LocalStorage,
			deserialize: deserializeMoment,
		}),
		NgxsReduxDevtoolsPluginModule.forRoot(),
		RoomsLibModule,
	],
	providers: [
		{
			provide: APP_INITIALIZER,
			useFactory: initializeApp,
			multi: true,
			deps: [ConfigurationService],
		},
		{
			provide: ROOMS_LIB_CONFIG,
			useFactory: (configService: ConfigurationService) => {
				const config = configService.getConfig();
				if (!config) {
					return;
				}
				const libConfig: RoomsLibConfig = {
					context: "ONSITE",
					rootUrl: config.rootConfig.roomsUrl,
					oauthConfig: {
						redirectUri: "",
						clientId: "rooms-onsite",
						scope: "openid rooms_api profile",
						postLogoutRedirectUri: "",
						authority: config.rootConfig.idpUrl,
					},
				};
				return libConfig;
			},
			deps: [ConfigurationService],
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true,
		},
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class CoreModule {
	constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
		if (parentModule) {
			throw new Error(
				`${parentModule.constructor.name} has already been loaded. Import this module in the AppModule only.`,
			);
		}
	}
}
